import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, OnDestroy, OnInit, signal, ViewChild} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {asyncScheduler, BehaviorSubject, combineLatest, EMPTY, Observable, of, ReplaySubject, Subject, timer} from 'rxjs';
import {
  AffiliateExclusiveOption,
  AffiliateMerchantDiscount,
  AffiliateRelationshipMerchant,
  CouponCodeRuleType,
  DiscountStatus,
  DiscountType,
  DownloadRequestBody,
  DownloadRequestContent,
  DownloadRequestContentColumn,
  MerchantDiscount,
  PagedRequest,
  ShopifyAuthorizationDetail,
  TagExclusiveOption
} from '../../core/dtos/api';
import {
  catchError,
  debounceTime,
  filter,
  first,
  map,
  observeOn,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import {DashboardApiService} from '../../core/services/api/dashboard-api.service';
import {MediaMatcher} from '@angular/cdk/layout';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormControl} from '@angular/forms';
import {DiscountApiService} from '../../core/services/api/discount-api.service';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {TagApiService} from '../../core/services/api/tag-api.service';
import {TitleCasePipe} from '@angular/common';
import {CreateDiscountDialogComponent} from './create-discount-dialog/create-discount-dialog.component';
import {allTableColumns, CreateDiscountResponse, createDiscountWidth, discountRuleTypeMap} from './discount-management';
import {AffiliateDetailsDialogComponent} from './affiliate-details-dialog/affiliate-details-dialog.component';
import {DashboardPermissionsService} from '../../dashboard/dashboard-permissions-service/dashboard-permissions.service';
import {ActivatedRoute, Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MerchantCommon} from '../ad-management/ad-management';
import {outPlaceSort} from '../../core/pure-utils/array-utils';
import {CompareDates, convertDateToDiffSinceText, stripHttpWWWFromUrl, utcToMtTime} from '../../core/pure-utils/string-utils';
import {AffiliateApiService} from '../../core/services/api/affiliate-api.service';
import {GetDiscountDialogComponent} from './get-discount-dialog/get-discount-dialog.component';
import {MatSnackBarRef, SimpleSnackBar} from '@angular/material/snack-bar';
import {SnackBarService} from '../../core-services/snack-bar/snack-bar.service';
import {environment} from '../../../environments/environment';
import {DashboardHttpService} from '../../core-services/dashboard-http-service/dashboard-http.service';
import {
  CustomizeTableColumnComponentData,
  CustomizeTableColumnsComponent,
  TableColumn
} from '../../shared/customize-table-columns/customize-table-columns.component';
import {MatIconButton} from '@angular/material/button';
import {triggerHotJarGa} from '../../core/pure-utils/analytics';
import {UserSettingsApiService} from '../../core/services/api/user-settings-api.service';
import {SelectionModel} from '@angular/cdk/collections';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {AreYouSureData, AreYouSureDialogComponent} from '../../shared/are-you-sure-dialog/are-you-sure-dialog.component';
import {SelectedItemsData, SelectedItemsDialogComponent} from '../../shared/selected-items-dialog/selected-items-dialog.component';
import {ShopifySyncDialogComponent, ShopifySyncDialogData} from './shopify-sync-dialog/shopify-sync-dialog.component';
import {MatSidenav} from '@angular/material/sidenav';
import {HttpErrorResponse} from '@angular/common/http';
import {ShopifyAuditLogDialogComponent} from './shopify-audit-log-dialog/shopify-audit-log-dialog.component';
import {
  subDays,
  subHours,
  subMinutes,
  subMonths,
  subWeeks,
  subYears
} from 'date-fns';

type FilterKey = 'titleInput' | 'detailsInput' | 'typeSelect' | 'affiliateAutoComplete' | 'merchantAutoComplete' | 'couponCodeInput' | 'ruleTypeSelect'
  | 'statusSelect';
type UpdatedDiscount = AffiliateMerchantDiscount & {showNewLabel: boolean; showCouponTooltip: boolean; ruleTypeTooltip: string; lastUpdatedAt: string};
interface FilterChip {control: FilterKey; value: string; displayValue: string}

@Component({
    selector: 'app-discounts',
    templateUrl: './discount-management.component.html',
    styleUrl: './discount-management.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DiscountManagementComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('customizeColumnTrigger') customizeColumnTriggerRef: MatIconButton;
  @ViewChild('sidenav') sideNav: MatSidenav;

  dataSource = signal([] as UpdatedDiscount[]);
  filterChips = signal([] as FilterChip[]);
  mobileQuery = signal(this.media.matchMedia('(max-width: 374.99px)'));
  drawerOpened = signal(true);
  isLoadingResults = signal(true);
  isDownloadingDiscounts = signal(false);
  dataError = signal(false);
  totalItems = signal(0);
  tableColumns = signal([] as string[]);
  showProcessingSync = signal(false);
  showRestrictedCoupons = signal(false);
  closeSideNavEffect = effect(() => {
    if (this.showProcessingSync() === true) {
      this.sideNav.close();
    }
  });

  readonly affiliateOptionLimit = 100;
  readonly tagGroupOptionLimit = 10;
  readonly merchantOptionLimit = 250;
  readonly allAffiliatesOption = <AffiliateExclusiveOption> { affiliate_name: 'All Affiliates', filterType: 'affiliates' };
  readonly allMerchantsOption = <MerchantCommon>{merchant_name: 'All Merchants'};
  readonly initialControlValues: Record<FilterKey, any> = {
    titleInput: '',
    detailsInput: '',
    typeSelect: 'all' as DiscountType | 'all',
    affiliateAutoComplete: this.allAffiliatesOption as AffiliateExclusiveOption,
    merchantAutoComplete: this.allMerchantsOption as MerchantCommon,
    couponCodeInput: '',
    ruleTypeSelect: 'all' as CouponCodeRuleType | 'all',
    statusSelect: 'active' as DiscountStatus | 'active',
  };
  readonly defaultControlValues: Record<FilterKey, any> = {
    titleInput: '',
    detailsInput: '',
    typeSelect: 'all' as DiscountType | 'all',
    affiliateAutoComplete: this.allAffiliatesOption as AffiliateExclusiveOption,
    merchantAutoComplete: this.allMerchantsOption as MerchantCommon,
    couponCodeInput: '',
    ruleTypeSelect: 'all' as CouponCodeRuleType | 'all',
    statusSelect: 'all' as DiscountStatus | 'all',
  };
  readonly controlsAssociatedData: Record<FilterKey, {displayTitle: string; queryKey: string}> = {
    titleInput: {displayTitle: 'Discount Title', queryKey: 'discount_name'},
    detailsInput: {displayTitle: 'Discount Details', queryKey: 'discount_description'},
    typeSelect: {displayTitle: 'Discount Type', queryKey: 'discount_type'},
    affiliateAutoComplete: {displayTitle: 'Affiliate', queryKey: 'discount_entity_id'},
    merchantAutoComplete: {displayTitle: 'Merchant', queryKey: 'merchant_id'},
    couponCodeInput: {displayTitle: 'Coupon Code', queryKey: 'coupon_code'},
    ruleTypeSelect: {displayTitle: 'Rule Type', queryKey: 'discount_rule_type_id'},
    statusSelect: {displayTitle: 'Status', queryKey: 'discount_status'}
  };

  readonly affiliateTableColumns = ['get_link_action', 'discount_name', 'discount_description', 'merchant_name',
    'coupon_code', 'discount_rule_type_name', 'started_at', 'expired_at', 'updated_at'];
  readonly merchantTableColumns = ['edit_action', 'discount_name', 'discount_description', 'discount_entity_name', 'coupon_code',
    'discount_rule_type_name', 'discount_status', 'started_at', 'expired_at', 'updated_at'];
  protected readonly environment = environment;
  selectedCustomColumns = [] as TableColumn[];
  requestParams = {} as PagedRequest;
  affiliateFilterPreviousInput = this.allAffiliatesOption;
  merchantFilterPreviousInput = this.allMerchantsOption;
  createDiscountSnackBarRef = null as MatSnackBarRef<SimpleSnackBar>;
  discountsSelection = new SelectionModel<UpdatedDiscount>(true, []);

  public triggerAnalytics = triggerHotJarGa;

  ngAfterViewInit$ = new ReplaySubject<void>(1);
  refreshShopifySettings$ = new BehaviorSubject<void>(null);
  refreshTableData$ = new Subject<void>();
  triggerFilterChange$ = new Subject<void>();
  triggerShopifySyncPolling$ = new Subject<void>();
  entityAccountGroupName$ = this.dashboardApiService.currentAccountGroup$;

  filterControls= {
    titleInput: this.fb.control<string>(this.initialControlValues.titleInput),
    detailsInput: this.fb.control<string>(this.initialControlValues.detailsInput),
    typeSelect: this.fb.control<DiscountType | 'all'>(this.initialControlValues.typeSelect),
    affiliateAutoComplete: this.fb.control<AffiliateExclusiveOption | string>(this.initialControlValues.affiliateAutoComplete),
    merchantAutoComplete: this.fb.control<MerchantCommon | string>(this.initialControlValues.merchantAutoComplete),
    couponCodeInput: this.fb.control<string>(this.initialControlValues.couponCodeInput),
    ruleTypeSelect: this.fb.control<CouponCodeRuleType | 'all' | 'none' | 'restricted'>(this.initialControlValues.ruleTypeSelect),
    statusSelect: this.fb.control<DiscountStatus | 'all'>(this.initialControlValues.statusSelect),
  };
  formGroup = this.fb.group(this.filterControls);
  filterControlValues = {
    titleInput$: this.filterControls.titleInput.valueChanges.pipe(startWith(this.initialControlValues.titleInput), debounceTime(300)),
    detailsInput$: this.filterControls.detailsInput.valueChanges.pipe(startWith(this.initialControlValues.detailsInput), debounceTime(300)),
    typeSelect$: this.filterControls.typeSelect.valueChanges.pipe(startWith(this.initialControlValues.typeSelect)),
    couponCodeInput$: this.filterControls.couponCodeInput.valueChanges.pipe(startWith(this.initialControlValues.couponCodeInput), debounceTime(300)),
    ruleTypeSelect$: this.filterControls.ruleTypeSelect.valueChanges.pipe(startWith(this.initialControlValues.ruleTypeSelect)),
    statusSelect$: this.filterControls.statusSelect.valueChanges.pipe(startWith(this.initialControlValues.statusSelect)),
    merchantAutoComplete$: this.filterControls.merchantAutoComplete.valueChanges.pipe(startWith(this.initialControlValues.merchantAutoComplete)),
    affiliateAutoComplete$: this.filterControls.affiliateAutoComplete.valueChanges.pipe(startWith(this.initialControlValues.affiliateAutoComplete)),
  };

  affiliateFilterControls = {
    seeRestricted: this.fb.control<Boolean>(false)
  };

  ruleTypeSelectOptions = [
    'all', 'exclusive', 'restricted', 'qualified'
  ];

  affiliateFilterControlValues = {
    seeRestricted$: this.affiliateFilterControls.seeRestricted.valueChanges.pipe(startWith(this.ruleTypeSelectOptions))
  };

  combinedFilters$ = combineLatest([
    this.filterControlValues.titleInput$,
    this.filterControlValues.detailsInput$,
    this.filterControlValues.typeSelect$,
    this.filterControlValues.couponCodeInput$,
    this.filterControlValues.ruleTypeSelect$,
    this.filterControlValues.statusSelect$,
    this.filterControlValues.merchantAutoComplete$.pipe(filter(x => typeof x !== 'string')),
    this.filterControlValues.affiliateAutoComplete$.pipe(filter(x => typeof x !== 'string')),
    this.affiliateFilterControlValues.seeRestricted$
  ]).pipe(
    map(([titleInput, detailsInput, typeSelect, couponCodeInput, ruleTypeSelect, statusSelect, merchantAutoComplete, affiliateAutoComplete]) =>
      ({titleInput, detailsInput, typeSelect, couponCodeInput, ruleTypeSelect, statusSelect, merchantAutoComplete, affiliateAutoComplete})),
  );

  changeFiltersOnAccountSwitch$Sub = this.entityAccountGroupName$.pipe(takeUntilDestroyed())
    .subscribe({
      next: (entityAccountGroupName) => {
        this.createDiscountSnackBarRef?.dismiss();
        this.formGroup.reset(this.initialControlValues, {emitEvent: false});
        this.affiliateFilterPreviousInput = this.initialControlValues.affiliateAutoComplete;
        this.merchantFilterPreviousInput = this.initialControlValues.merchantAutoComplete;
        let relatedKeys = entityAccountGroupName === 'affiliates'
          ? Object.keys(this.initialControlValues).filter(key => key !== 'statusSelect') : Object.keys(this.initialControlValues);
        let filterChips = relatedKeys.reduce((acc, key) => {
          if (this.initialControlValues[key] !== this.defaultControlValues[key]) {
            acc.push({
              control: key,
              displayValue: `${this.controlsAssociatedData[key].displayTitle} = ${this.titleCasePipe.transform(this.initialControlValues[key])}`,
              value: this.initialControlValues[key]
            } as FilterChip);
          }
          return acc;
        }, [] as FilterChip[]);
        this.filterChips.set(filterChips);
      }
    });
  changeFiltersOnFilterChange$Sub = this.combinedFilters$.pipe(
    withLatestFrom(this.entityAccountGroupName$),
    takeUntilDestroyed()
  ).subscribe({
    next: ([, entityAccountGroupName]) => {
      let combinedFilters = Object.keys(this.filterControls).reduce((acc, key) => {
        acc[key] = this.filterControls[key].value;
        return acc;
      }, {} as Record<string, any>);
      let filteredControls = entityAccountGroupName === 'affiliates'
        ? Object.keys(combinedFilters).filter(controlKey => controlKey !== 'affiliateAutoComplete' && controlKey !== 'statusSelect')
        : entityAccountGroupName === 'merchants' ? Object.keys(combinedFilters).filter(controlKey => controlKey !== 'merchantAutoComplete') : [];
      let filterChips = filteredControls.reduce((acc, controlKey) => {
        if (combinedFilters[controlKey] !== this.defaultControlValues[controlKey]) {
          let value = '';
          let displayValue = '';
          if (controlKey === 'affiliateAutoComplete') {
            if (combinedFilters[controlKey] !== this.allAffiliatesOption) {
              if (combinedFilters[controlKey].filterType === 'affiliates') {
                displayValue = `Affiliate = ${this.titleCasePipe.transform(combinedFilters[controlKey].affiliate_name)}`;
                value = combinedFilters[controlKey].affiliate_id;
              } else {
                displayValue = `Tag Group = ${this.titleCasePipe.transform(combinedFilters[controlKey].tag_name)}`;
                value = combinedFilters[controlKey].tag_id;
              }
            }
          } else if (controlKey === 'merchantAutoComplete' && combinedFilters[controlKey] !== this.allMerchantsOption) {
            displayValue = `Merchant = ${combinedFilters[controlKey].merchant_name}`;
            value = combinedFilters[controlKey].merchant_id;
          } else if (controlKey === 'ruleTypeSelect') {
            displayValue = `${this.controlsAssociatedData[controlKey].displayTitle} = ${this.titleCasePipe.transform(combinedFilters[controlKey])}`;
            value = discountRuleTypeMap[combinedFilters[controlKey]];
          } else {
            displayValue = `${this.controlsAssociatedData[controlKey].displayTitle} = ${this.titleCasePipe.transform(combinedFilters[controlKey])}`;
            value = combinedFilters[controlKey];
          }
          acc.push({
            control: controlKey as FilterKey, displayValue, value
          });
        }
        return acc;
      }, [] as FilterChip[]);
      this.filterChips.set(filterChips);
      this.triggerFilterChange$.next();
    }
  });

  changeAffiliateView$Sub = this.affiliateFilterControlValues.seeRestricted$.pipe(
    withLatestFrom(this.entityAccountGroupName$),
    takeUntilDestroyed()
  ).subscribe(
    ([, entityAccountGroupName]) => {
      if(entityAccountGroupName === 'affiliates') {
        if (this.affiliateFilterControls.seeRestricted.value === false) {
          this.ruleTypeSelectOptions = [
            'all', 'exclusive', 'qualified'
          ];
          this.showRestrictedCoupons.set(false);
          this.filterControls.ruleTypeSelect.setValue('all');
        } else {
          this.ruleTypeSelectOptions = [
            'all', 'exclusive', 'restricted', 'qualified'
          ];
          this.showRestrictedCoupons.set(true);
          this.filterControls.ruleTypeSelect.setValue('restricted');
        }
      }
    }
  );

  accountPermissions$Sub = this.dashboardPermissionsService.accountWithPermissions$.pipe(takeUntilDestroyed())
    .subscribe(([currentAccount, accountPermissions]) => {
      let foundPermission = accountPermissions.some((singlePermission) =>
        (singlePermission.permission_name === 'configure_tools' && singlePermission.has_permission)
        || (singlePermission.permission_name === 'merchant_access' && singlePermission.has_permission));
      if (!foundPermission) {
        this.router.navigate(['/access-denied']);
      }
    });
  tableColumns$Sub = this.entityAccountGroupName$.pipe(takeUntilDestroyed())
    .subscribe((groupName) => {
    if (groupName === 'merchants') {
      this.tableColumns.set(this.merchantTableColumns);
    } else if (groupName === 'affiliates') {
      this.tableColumns.set(this.affiliateTableColumns);
    } else {
      this.tableColumns.set([]);
    }
  });

  currentAccountGroupAndDiscountSettings$ = combineLatest([this.dashboardApiService.currentAccountGroup$, this.userSettingsApiService.getDiscountColumnSettings$]);
  setSelectedColumnsForTable$Sub = this.currentAccountGroupAndDiscountSettings$.pipe(takeUntilDestroyed()).subscribe(([accountGroup, savedDiscountColumn]) => {
    // adding filter to remove discount type column from the results as we are no longer loading the discount type column in the view
    let savedDiscountColumns: string[] = accountGroup === 'merchants' ? savedDiscountColumn[accountGroup]
    .filter(column => column !== 'discount_type') : savedDiscountColumn[accountGroup];
    let columnNames = savedDiscountColumns as string[] ?? allTableColumns
      .filter(column => column.entityType.includes(accountGroup))
      .map(column => column.name);
    columnNames.unshift(accountGroup === 'affiliates' ? 'get_link_action' : 'edit_action');
    this.tableColumns.set(columnNames);
  });
  setSelectionColumnsForDialog$Sub = this.currentAccountGroupAndDiscountSettings$.pipe(
    map(([accountGroup, savedDiscountColumns]) => {
      if (accountGroup.includes('affiliates') || accountGroup.includes('merchants')) {
        if (savedDiscountColumns[accountGroup]) {
          return savedDiscountColumns[accountGroup].reduce((acc, curr) => {
            let targetedColumn = allTableColumns.find(column => column.entityType.includes(accountGroup) && column.name === curr);
            if (targetedColumn) {
              acc.push(targetedColumn);
            }
            return acc;
          }, [] as TableColumn[]);
        }
        return allTableColumns.filter(column => column.entityType.includes(accountGroup));
      }
      return [] as TableColumn[];
    }),
    takeUntilDestroyed()
  ).subscribe(tableColumns => {
    this.selectedCustomColumns = tableColumns;
  });

  deleteDialogRef: MatDialogRef<SelectedItemsDialogComponent<UpdatedDiscount>, boolean>;
  discounts$ = this.discountsSelection.changed.pipe(map(x => x.source.selected), shareReplay({bufferSize: 1, refCount: true}));
  openDeleteDialog$Sub = this.discounts$.pipe(
    filter((discountsSelection) => discountsSelection.length > 0 && this.deleteDialogRef === undefined),
    switchMap(() => {
      let selectedItemsData: SelectedItemsData<UpdatedDiscount> = {
        descriptionTitle: 'Discounts Selected ',
        buttons: [
          {display: 'Delete', associatedFunc: this.onDeleteButtonClick},
        ],
        items$: this.discounts$
      };
      this.deleteDialogRef = this.matDialog.open(SelectedItemsDialogComponent<UpdatedDiscount>, {
        data: selectedItemsData,
        hasBackdrop: false,
        restoreFocus: false,
        panelClass: 'selection-dialog-panel',
        position: {bottom: '4px', left:'calc(50% - 200px)'},
        height:'64px',
        width: '400px',
        minWidth: '400px'
      });
      return this.deleteDialogRef.afterClosed();
    }),
    takeUntilDestroyed()
  ).subscribe(() => {
    this.deleteDialogRef = undefined;
    this.discountsSelection.clear();
    this.detectChangesRef.detectChanges();
  });
  closeDeleteDialog$Sub = this.discounts$.pipe(
    filter((discountsSelection) => discountsSelection.length === 0 && this.deleteDialogRef !== undefined),
    takeUntilDestroyed()
  ).subscribe(() => {
    this.deleteDialogRef.close();
  });

  dataSource$Sub = this.ngAfterViewInit$.pipe(
    switchMap(() => combineLatest([
      this.triggerFilterChange$,
      this.sort.sortChange.pipe(debounceTime(250), startWith({}), tap(() => this.paginator.pageIndex = 0)),
      this.paginator.page.pipe(debounceTime(250), startWith({})),
      this.refreshTableData$.pipe(tap(() => this.paginator.pageIndex = 0))
    ]).pipe(withLatestFrom(this.dashboardApiService.currentAccount$))),
    switchMap(([, currentAccount]) => {
      this.isLoadingResults.set(true);
      this.discountsSelection.clear();
      const compareDates: CompareDates = {
        now: utcToMtTime(new Date()),
        minuteBack: utcToMtTime(subMinutes(new Date(), 1)),
        hourBack: utcToMtTime(subHours(new Date(), 1)),
        dayBack: utcToMtTime(subDays(new Date(), 1)),
        weekBack: utcToMtTime(subWeeks(new Date(), 1)),
        monthBack: utcToMtTime(subMonths(new Date(), 1)),
        yearBack: utcToMtTime(subYears(new Date(), 1))
      };
      let params = {
        sort: this.sort.active,
        direction: this.sort.direction.toUpperCase(),
        page: this.paginator.pageIndex + 1,
        limit: this.paginator.pageSize
      };
      this.filterChips().forEach(filterChip => {
        params[this.controlsAssociatedData[filterChip.control].queryKey] = filterChip.value;
      });
      this.requestParams = params;
      if (currentAccount.entity_group_name === 'merchants') {
        return this.discountApiService.getPagedMerchantDiscounts(currentAccount.entity_id, params).pipe(
          map(data => {
            this.isLoadingResults.set(false);
            this.dataError.set(false);
            this.totalItems.set(data.total_items);
            let merchantDiscounts = data.items.map(discount => {
              let updatedDate = new Date(discount.updated_at);
              let nowInMtTime = utcToMtTime(Date.now());
              let showNewLabel = discount.updated_at !== null && ((nowInMtTime.getTime() - updatedDate.getTime()) < (86400000 * 7));
              let showCouponTooltip = discount.coupon_code !== null && discount.coupon_code?.includes('*') && discount.discount_source !== 'shopify';
              let rule_type = discount.discount_rule_type;
              let ruleTypeTooltip = '';
              if (rule_type !== null) {
                switch (rule_type.discount_rule_type_name) {
                  case 'Exclusive':
                    ruleTypeTooltip = 'Commission an affiliate on all orders that use a specific coupon code';
                    break;

                  case 'Qualified':
                    ruleTypeTooltip = 'Commission specific affiliate(s) when both an affiliate click and a coupon code are used on an order';
                    break;

                  case 'Restricted':
                    ruleTypeTooltip = 'Prevent affiliates from earning commission when a specific coupon code is used';
                    break;

                  default:
                    break;
                }
              }
              let lastUpdatedAt = convertDateToDiffSinceText(updatedDate, compareDates);
              return {...discount, showNewLabel, showCouponTooltip, ruleTypeTooltip, lastUpdatedAt} as UpdatedDiscount;
            }) as UpdatedDiscount[];
            this.dataSource.set(merchantDiscounts);
            return merchantDiscounts;
          }),
          catchError(() => {
            this.isLoadingResults.set(false);
            this.dataError.set(true);
            this.totalItems.set(0);
            this.dataSource.set([] as UpdatedDiscount[]);
            return of<UpdatedDiscount[]>([]);
          })
        );
      } else {
        return this.discountApiService.getAffiliateDiscounts(currentAccount.entity_id, params, this.affiliateFilterControls.seeRestricted.value).pipe(
          map(data => {
            this.isLoadingResults.set(false);
            this.dataError.set(false);
            this.totalItems.set(data.total_items);
            let itemsWithShowCouponTooltip = data.items.map(discount => {
              let updatedDate = new Date(discount.updated_at);
              let nowInMtTime = utcToMtTime(Date.now());
              let showNewLabel = discount.updated_at !== null && ((nowInMtTime.getTime() - updatedDate.getTime()) < (86400000 * 7));
              let showCouponTooltip = discount.coupon_code !== null && discount.coupon_code?.includes('*') && discount.discount_source !== 'shopify';
              let rule_type = discount.discount_rule_type;
              let ruleTypeTooltip = '';
              if (rule_type !== null) {
                switch (rule_type.discount_rule_type_name) {
                  case 'Exclusive':
                    ruleTypeTooltip = `Commissions will be awarded on all orders that use this coupon code -
                    commission will be awarded even if there is no click associated`;
                    break;

                  case 'Qualified':
                    ruleTypeTooltip = `In order to earn commission with this code both an affiliate click and a coupon code is needed on the sale -
                    commission will not be awarded if the coupon code is used without a click`;
                    break;

                  case 'Restricted':
                    ruleTypeTooltip = `Commission will not be earned on sales with this specific coupon code -
                    order will still track for reporting`;
                    break;

                  default:
                    break;
                }
              }
              let lastUpdatedAt = convertDateToDiffSinceText(updatedDate, compareDates);
              return {...discount, showNewLabel, showCouponTooltip, ruleTypeTooltip, lastUpdatedAt} as UpdatedDiscount;
            }) as UpdatedDiscount[];
            this.dataSource.set(itemsWithShowCouponTooltip);
            return itemsWithShowCouponTooltip;
          }),
          catchError(() => {
            this.isLoadingResults.set(false);
            this.dataError.set(true);
            this.totalItems.set(0);
            this.dataSource.set([] as UpdatedDiscount[]);
            return of<UpdatedDiscount[]>([]);
          })
        );
      }
    }),
    takeUntilDestroyed(),
    tap(() => this.showProcessingSync.set(false))
  ).subscribe();

  triggerShopifySyncPolling$Sub = this.triggerShopifySyncPolling$.pipe(
    switchMap(() => combineLatest([timer(1, 3000), this.dashboardApiService.currentAccount$]).pipe(
        switchMap(([, currentAccount]) => this.userSettingsApiService.getShopifySyncInProgressSetting(currentAccount.entity_id)),
        map(shopifyInProgressSetting => shopifyInProgressSetting !== null && shopifyInProgressSetting?.key_value === true),
        filter(isProcessing => isProcessing === false),
        take(1)
      )
    ),
    takeUntilDestroyed()
  ).subscribe({
      next: () => {
        this.refreshShopifySettings$.next();
      },
      error: (error: HttpErrorResponse) => this.snackBarService.openErrorSnackBar(error.error.error)
    }
  );

  constructor(private dashboardApiService: DashboardApiService, private media: MediaMatcher, private matDialog: MatDialog,
              private fb: FormBuilder, private titleCasePipe: TitleCasePipe, private discountApiService: DiscountApiService,
              private tagApiService: TagApiService, private dashboardPermissionsService: DashboardPermissionsService, private router: Router,
              private affiliateApiService: AffiliateApiService, private route: ActivatedRoute, private snackBarService: SnackBarService,
              private dashboardHttpService: DashboardHttpService, private userSettingsApiService: UserSettingsApiService,
              private detectChangesRef: ChangeDetectorRef) {
  }

  ngAfterViewInit(): void {
    this.ngAfterViewInit$.next();
    this.ngAfterViewInit$.complete();
  }
  ngOnDestroy(): void {
    this.createDiscountSnackBarRef?.dismiss();
    this.deleteDialogRef?.close();
  }

  usersAffiliates$ = this.dashboardApiService.usersAffiliates$;
  affiliateFilteredOptions$ = combineLatest([
    this.filterControls.affiliateAutoComplete.valueChanges, this.usersAffiliates$]).pipe(
    map(([affiliateFilter, affiliateOptions]) => {
      if (affiliateFilter === '' || typeof affiliateFilter !== 'string') {
        return affiliateOptions;
      }

      let lowerInput = affiliateFilter.toLowerCase();
      return affiliateOptions.filter(x => x.affiliate_name.toLowerCase().indexOf(lowerInput) !== -1 ||
        x.affiliate_website_url_protocol_stripped.indexOf(lowerInput) !== -1 ||
        String(x.classic_affiliate_id).indexOf(lowerInput) !== -1);
    }),
    observeOn(asyncScheduler),
    shareReplay({bufferSize: 1, refCount: true}));
  affiliateFilteredOptionsLimited$ = this.affiliateFilteredOptions$.pipe(
    map(x => x.length > this.affiliateOptionLimit ? x.slice(0, this.affiliateOptionLimit) : x));

  usersTagGroups$ = this.tagApiService.usersTagGroups$;
  userTagFilteredOptions$ = combineLatest([this.filterControls.affiliateAutoComplete.valueChanges, this.usersTagGroups$]).pipe(
    map(([exclusiveFilter, tagOptions]) => {
      if (exclusiveFilter === '' || typeof exclusiveFilter !== 'string') {
        return tagOptions;
      }

      let lowerInput = exclusiveFilter.toLowerCase();
      return tagOptions.filter(x => x.tag_name.toLowerCase().indexOf(lowerInput) !== -1);
    }),
    observeOn(asyncScheduler),
    shareReplay({bufferSize: 1, refCount: true}));
  userTagFilteredOptionsLimited$ = this.userTagFilteredOptions$.pipe(
    map(x => x.length > this.tagGroupOptionLimit ? x.slice(0, this.tagGroupOptionLimit) : x));

  merchantFilterOptions$: Observable<MerchantCommon[]> =
    this.dashboardApiService.currentAccount$.pipe(
      switchMap((currentAccount) => {
        if (currentAccount.entity_group_name !== 'affiliates') {
          return of<AffiliateRelationshipMerchant[]>([]);
        }
        return this.affiliateApiService.getAllAssociatedMerchantWebsitesOfAffiliate([currentAccount.entity_id], 'active').pipe(
          map(xs => xs.filter(x => x.is_default && x.merchant_status === 'active')));
      }),
      map(xs => outPlaceSort(xs, x => x.merchant_name)
        .map(x => ({...x, merchantWebsiteUrlStripped: stripHttpWWWFromUrl(x.merchant_website_url)}))),
      shareReplay({bufferSize: 1, refCount: true}));
  merchantFilteredOptions$ = combineLatest([this.filterControls.merchantAutoComplete.valueChanges, this.merchantFilterOptions$]).pipe(
    map(([merchantFilter, merchantOptions]) => {
      if (merchantFilter === '' || typeof merchantFilter !== 'string') {
        return merchantOptions;
      }
      let lowerInput = merchantFilter.toLowerCase();
      return merchantOptions.filter(x => x.merchant_name.toLowerCase().includes(lowerInput) ||
        (x.merchantWebsiteUrlStripped !== null && x.merchantWebsiteUrlStripped.includes(lowerInput)) ||
        String(x.classic_merchant_id).includes(lowerInput));
    }),
    observeOn(asyncScheduler),
    shareReplay({bufferSize: 1, refCount: true}));
  merchantFilteredOptionsLimited$ = this.merchantFilteredOptions$.pipe(
    map(x => x.length > this.merchantOptionLimit ? x.slice(0, this.merchantOptionLimit) : x));

  onRemoveChipClick(chip: FilterChip) {
    this.filterControls[chip.control].setValue(this.defaultControlValues[chip.control]);
    if (chip.control === 'affiliateAutoComplete') {
      this.affiliateFilterPreviousInput = this.defaultControlValues[chip.control];
    }
    if (chip.control === 'merchantAutoComplete') {
      this.merchantFilterPreviousInput = this.defaultControlValues[chip.control];
    }
    if(chip.control === 'ruleTypeSelect') {
      this.affiliateFilterControls.seeRestricted.setValue(false);
    }
  }

  // The displayWith requires we use arrow syntax, so we can access the titleCasePipe class property.
  merchantFilterDisplayWith = (value: string | MerchantCommon) =>
    typeof value === 'string' ? value === this.allMerchantsOption.merchant_name ? 'All Merchants' : value : value.merchant_name.trim();
  onSelectedMerchantFilterAutoComplete = ($event: MatAutocompleteSelectedEvent, filteredMerchantsInput: HTMLInputElement) => {
    this.merchantFilterPreviousInput = $event.option.value;
    filteredMerchantsInput.blur();
  };
  onOpenedAutoSelect = (formControl: FormControl) => formControl.setValue('');
  onClosedMerchantFilterAutoComplete = () => {
    if (typeof this.filterControls.merchantAutoComplete.value === 'string') {
      this.filterControls.merchantAutoComplete.setValue(this.merchantFilterPreviousInput);
    }
  };

  // The displayWith requires we use arrow syntax, so we can access the titleCasePipe class property.
  affiliateFilterDisplayWith = (value: AffiliateExclusiveOption | TagExclusiveOption): string => {
    if (typeof value === 'string') {
      return value;
    }
    if (value.filterType === 'affiliates') {
      return value.affiliate_name.trim();
    }
    if (value.filterType === 'tag_groups') {
      return this.titleCasePipe.transform(`${value.tag_name.trim()} Tag Group`);
    }
    return value;
  };
  onSelectedAffiliateFilterAutoComplete($event: MatAutocompleteSelectedEvent, filteredAffiliateInput: HTMLInputElement) {
    this.affiliateFilterPreviousInput = $event.option.value;
    filteredAffiliateInput.blur();
  };
  onOpenAutoSelect = (formControl: FormControl) => formControl.setValue('');
  merchantEntitySettings$ = combineLatest([this.dashboardApiService.currentAccount$, this.refreshShopifySettings$]).pipe(
    filter(([currentAccount,]) => currentAccount.entity_group_name === 'merchants' || currentAccount.entity_group_name === 'affiliates'),
    switchMap(([currentAccount]) => this.userSettingsApiService.getAllEntitySettingsByGroupAndId(currentAccount.entity_id, currentAccount.entity_group_name)),
    takeUntilDestroyed(),
    shareReplay({bufferSize: 1, refCount: true})
  );
  isShopifyClient$ = this.merchantEntitySettings$.pipe(
    map((settings) => settings.some(x => x.key_name === 'ShopifyAuthorizationDetails' && x.key_value?.length > 0))
  );
  isShopifySyncOn$ = this.merchantEntitySettings$.pipe(
      map((settings) => settings.some(x => x.key_name === 'ShopifyDiscountsSyncing' && x.key_value === true))
  );
  isShopifySyncProcessing$ = this.merchantEntitySettings$.pipe(
    map((settings) => settings.some(x => x.key_name === 'ShopifySyncInProgress' && x.key_value === true))
  );

  shopifySyncError$Sub = this.merchantEntitySettings$.pipe(
    filter((settings) => settings.some(x => x.key_name === 'ShopifySyncInProgressLastError' && x.key_value)),
    switchMap((settings) =>
      of(settings.find(setting => setting.key_name === 'ShopifySyncInProgressLastError' && setting.key_value))
        .pipe(withLatestFrom(this.dashboardApiService.currentAccount$))),
    tap(([errorSetting]) => this.snackBarService.openPersistentSnackBar(errorSetting.key_value, 'error-snackbar')),
    switchMap(([, currentAccount]) => this.userSettingsApiService.putShopifySyncInProgressLastError(currentAccount.entity_id, '')),
    takeUntilDestroyed()
  ).subscribe();

  startShopifySyncPolling$Sub = this.isShopifySyncProcessing$.pipe(takeUntilDestroyed()).subscribe((shouldStartPolling) => {
    if (shouldStartPolling) {
      this.showProcessingSync.set(true);
      this.triggerShopifySyncPolling$.next();
    } else {
      this.refreshTableData$.next();
    }
  });

  onCloseAffiliateFilterAutoComplete() {
    if (typeof this.filterControls.affiliateAutoComplete.value === 'string') {
      this.filterControls.affiliateAutoComplete.setValue(this.affiliateFilterPreviousInput);
    }
  };
  onClickCreateDiscountButton(): void {
    let dialogRef
      = this.matDialog.open<CreateDiscountDialogComponent, {merchantDiscount: MerchantDiscount}, CreateDiscountResponse>(
        CreateDiscountDialogComponent,
        {data: {} as {merchantDiscount: MerchantDiscount}, disableClose: true, width: createDiscountWidth}
    );

    dialogRef.afterClosed().subscribe((closeResponse: CreateDiscountResponse) => {
      this.createDiscountSnackBarRef = closeResponse.snackBarRef;
      if (closeResponse.refreshTable) {
        this.refreshTableData$.next();
      }
    });
  }
  onEditDiscountClick(row: MerchantDiscount) {
    let dialogRef
      = this.matDialog.open<CreateDiscountDialogComponent, {merchantDiscount: MerchantDiscount}, CreateDiscountResponse>(
        CreateDiscountDialogComponent,
        {data: {merchantDiscount: row}, disableClose: true}
    );

    dialogRef.afterClosed().subscribe((closeResponse: CreateDiscountResponse) => {
      this.createDiscountSnackBarRef = closeResponse.snackBarRef;
      if (closeResponse.refreshTable) {
        this.refreshTableData$.next();
      }
    });
  }
  onClickAffiliates(row: MerchantDiscount) {
    let dialogRef = this.matDialog.open(AffiliateDetailsDialogComponent, {data: {merchantDiscount: row}});
  }
  onGetLinkClick(row: AffiliateMerchantDiscount) {
    let dialogRef = this.matDialog.open(GetDiscountDialogComponent, {data: {affiliateMerchantDiscount: row}, width: '512px'});
  }

  ngOnInit(): void {
    combineLatest([this.route.queryParamMap.pipe(
      filter(queryParams => queryParams.has('merchantDiscountId'))
    ), this.dashboardApiService.currentAccount$.pipe(filter(currentAccount => currentAccount.entity_group_name === 'affiliates'))]).pipe(
      switchMap(([queryParams, currentAccount]) => {
        let discountId = queryParams.get('merchantDiscountId');
        return this.discountApiService.getAffiliateMerchantDiscountById(currentAccount.entity_id, discountId);
      }),
      map(data => data),
      take(1)
    ).subscribe( {
      next: (discounts) => {
        this.onGetLinkClick(discounts);
      },
      error: () => this.snackBarService.openErrorSnackBar('Unable to find discount.')
    });
  }

  onDownloadClick() {
    const merchantColumns: DownloadRequestContentColumn[] = [
      {column: 'discount_id', header: 'Discount Id'},
      {column: 'discount_name', header: 'Discount Title'},
      {column: 'discount_description', header: 'Discount Details'},
      {column: 'discount_type', header: 'Discount Type'},
      {column: 'discount_entity_name', header: 'Affiliates'},
      {column: 'coupon_code', header: 'Coupon Code'},
      {column: 'discount_rule_type.discount_rule_type_name', header: 'Rule Type'},
      {column: 'discount_status', header: 'Status'},
      {column: 'started_at', header: 'Start Date'},
      {column: 'expired_at', header: 'End Date'},
      {column: 'sort_updated_at', header: 'Last Updated'},
      {column: 'discount_url', header: 'Discount URL'}
    ];

    const affiliateColumns: DownloadRequestContentColumn[] = [
      {column: 'discount_id', header: 'Discount Id'},
      {column: 'discount_name', header: 'Discount Title'},
      {column: 'discount_description', header: 'Discount Details'},
      {column: 'discount_type', header: 'Discount Type'},
      {column: 'merchant.merchant_name', header: 'Merchant'},
      {column: 'coupon_code', header: 'Coupon Code'},
      {column: 'discount_rule_type.discount_rule_type_name', header: 'Rule Type'},
      {column: 'started_at', header: 'Start Date'},
      {column: 'expired_at', header: 'End Date'},
      {column: 'sort_updated_at', header: 'Last Updated'}
    ];

    this.dashboardApiService.currentAccount$.pipe(
      tap(() => this.isDownloadingDiscounts.set(true)),
      map(currentAccount => {
        if (currentAccount.entity_group_name === 'affiliates') {
          let params = { ...this.requestParams,
            sort: this.requestParams.sort && this.requestParams.direction
              ? this.requestParams.sort === 'discount_rule_type_name' ? 'drt.' + this.requestParams.sort
                : this.requestParams.sort === 'merchant_name' ? 'm.' + this.requestParams.sort
                : 'dv.' + this.requestParams.sort : 'dv.updated_at',
            direction: this.requestParams.direction ?  this.requestParams.direction.toUpperCase() : 'DESC',
            exclude_discount_rule_type_id: this.affiliateFilterControls.seeRestricted.value === false ? 'ac581f0e-e419-4d86-a45f-2fb5a1d490e2' : '',
            discount_rule_type_id: this.affiliateFilterControls.seeRestricted.value === true ? 'ac581f0e-e419-4d86-a45f-2fb5a1d490e2' : ''
          };
          return [affiliateColumns, `${environment.apiUrl}/affiliates/${currentAccount.entity_id}/discounts`, params] as const;
        }
        if (currentAccount.entity_group_name === 'merchants') {
          let params = { ...this.requestParams,
            sort: this.requestParams.sort && this.requestParams.direction
              ? this.requestParams.sort === 'discount_rule_type_name' ? 'drt.' + this.requestParams.sort : 'dv.' + this.requestParams.sort : 'dv.updated_at',
            direction: this.requestParams.direction ?  this.requestParams.direction.toUpperCase() : 'DESC',
          };
          return [merchantColumns, `${environment.apiUrl}/merchants/${currentAccount.entity_id}/discounts`, params] as const;
        }
        return [[], '', {}] as [DownloadRequestContentColumn[], string, PagedRequest];
      }),
      switchMap(([columns, requestUrl, params]) => {
        // Remove the page and limit property from params.
        let {page, limit, ...updateParams} = params;

        let paramsForURL = new URLSearchParams(Object.entries(updateParams) as string[][]).toString();
        let requestContent: DownloadRequestContent = {
          request_url: `${requestUrl}?${paramsForURL}`,
          columns,
          format: 'csv'
        };
        let downloadRequestBody: DownloadRequestBody = {
          request: JSON.stringify(requestContent)
        };
        return this.dashboardHttpService.downloadPostBlobWithOptions(`${environment.apiUrl}/download`, downloadRequestBody, 'merchant-discounts');
      }),
      tap(() => this.isDownloadingDiscounts.set(false)),
      first()
    ).subscribe();
  }

  onCustomizeDisplayClick() {
    let currentAccountAndDialogRef$ = this.dashboardApiService.currentAccount$.pipe(
      map((currentAccount) => {
        if (currentAccount.entity_group_name === 'affiliates') {
          let allAffiliateColumns = allTableColumns.filter(column => column.entityType.includes('affiliates'));
          return ([currentAccount, allAffiliateColumns] as const);
        }
        if (currentAccount.entity_group_name === 'merchants') {
          let allMerchantColumns = allTableColumns.filter(column => column.entityType.includes('merchants'));
          return ([currentAccount, allMerchantColumns] as const);
        }
        return ([currentAccount, [] as TableColumn[]] as const);
      }),
      first(),
      switchMap(([currentAccount, allTableOptions]) => {
        let matDialogRef = this.matDialog.open<CustomizeTableColumnsComponent, CustomizeTableColumnComponentData>(
          CustomizeTableColumnsComponent,
          {
            data: {triggerElement: this.customizeColumnTriggerRef._elementRef, selectedColumns: this.selectedCustomColumns, allOptions: allTableOptions},
            backdropClass: 'customize-columns-backdrop',
            panelClass: 'customize-columns-panel'
          });
        return (combineLatest([of(currentAccount), matDialogRef.componentInstance.columnChange]));
      }),
      shareReplay({bufferSize: 1, refCount: true}),
    );

    let setColumns$Sub = currentAccountAndDialogRef$.subscribe(([currentAccount, columnChange]) => {
      this.selectedCustomColumns = columnChange;
      let columnNames = this.selectedCustomColumns.map(column => column.name);
      columnNames.unshift(currentAccount.entity_group_name === 'affiliates' ? 'get_link_action' : 'edit_action');
      this.tableColumns.set(columnNames);
    });
    let saveColumns$Sub = currentAccountAndDialogRef$.pipe(
      withLatestFrom(this.userSettingsApiService.getDiscountColumnSettings$),
      debounceTime(300),
      switchMap(([[currentAccount, columnChange], savedColumnSettings]) => {
        let changedColumnKeys = columnChange.map(column => column.name);
        let columnSettings = savedColumnSettings;
        columnSettings[currentAccount.entity_group_name] = changedColumnKeys;
        return this.userSettingsApiService.putDiscountColumnsSetting(currentAccount.user_id, columnSettings);
      })
    ).subscribe();
  }

  onMasterToggleChange(matCheckboxChange: MatCheckboxChange) {
    if (matCheckboxChange.checked === true) {
      this.discountsSelection.select(...this.dataSource() as UpdatedDiscount[]);
    } else {
      this.discountsSelection.clear();
    }
  }

  onDeleteButtonClick = (selectedDiscounts: MerchantDiscount[]) => {
    let areYouSureRef = this.matDialog.open<AreYouSureDialogComponent, AreYouSureData, boolean>(
      AreYouSureDialogComponent,
      {
        data: {
          header: 'Delete Discounts',
          message: 'Are you sure you want to delete these discounts?',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Delete'
        }
      });

    let areYouSureClose$Sub = areYouSureRef.afterClosed().pipe(
      withLatestFrom(this.dashboardApiService.currentAccount$),
      switchMap(([wasDeleteConfirmed, currentAccount]) => {
        if(wasDeleteConfirmed && this.deleteDialogRef) {
          return this.discountApiService.deleteMultipleMerchantDiscounts(currentAccount.entity_id, selectedDiscounts);
        }
        return EMPTY;
      }),
      take(1)
    ).subscribe(() => {
      this.deleteDialogRef.close();
      this.refreshTableData$.next();
      this.snackBarService.openSnackBar('Discounts Deleted');
    });
  };

  onClickOpenShopifyModal() {
    this.merchantEntitySettings$.pipe(
      take(1),
      map(merchantEntitySettings => {
        let shopifyDiscountsSyncing = merchantEntitySettings.find(setting => setting.key_name === 'ShopifyDiscountsSyncing');
        let shopifyDiscountsSyncDefaults = merchantEntitySettings.find(setting => setting.key_name === 'ShopifyDiscountsSyncDefaults');
        let shopifyShopDetails = merchantEntitySettings.find(setting => setting.key_name === 'ShopifyAuthorizationDetails');
        let shopifyShopNames: ShopifyAuthorizationDetail[] = shopifyShopDetails?.key_value && shopifyShopDetails?.key_value.length > 0
          ? shopifyShopDetails.key_value : [];
        return {
          shopifyDiscountsSyncing: (shopifyDiscountsSyncing?.key_value as boolean) ?? false,
          automaticDiscountsAllAffiliates: (shopifyDiscountsSyncDefaults !== undefined)
            ? (shopifyDiscountsSyncDefaults?.key_value?.automatic_discounts_all_affiliates as boolean) : null,
          couponDiscountsAllAffiliates: (shopifyDiscountsSyncDefaults !== undefined)
            ? (shopifyDiscountsSyncDefaults?.key_value?.coupon_discounts_all_affiliates as boolean) : null,
          selectedSyncingStoreName: (shopifyDiscountsSyncDefaults?.key_value?.selected_syncing_store_name) ?? null,
          shopifyShopNames
        } as ShopifySyncDialogData;
      }),
      switchMap(shopifySyncDialogData => {
        let dialogRef = this.matDialog.open(ShopifySyncDialogComponent, {
          width: '848px',
          disableClose: true,
          data: shopifySyncDialogData,
          autoFocus: false
        });
        return dialogRef.afterClosed();
      })
    ).subscribe(afterClosedResponse => {
      if (afterClosedResponse) {
        this.refreshShopifySettings$.next();
      }
    });
  }

  onOpenShopifySyncLog() {
    let dialogRef = this.matDialog.open(ShopifyAuditLogDialogComponent, {data: {merchantDiscount: {}}, autoFocus: false});
  }
}
